import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteWorkshop } from "../../../state/workshopSlice";
import style from "./WorkshopMenu.module.css";
import EditIcon from "@mui/icons-material/Edit";
import Groups2Icon from "@mui/icons-material/Groups2";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PageviewIcon from "@mui/icons-material/Pageview";
import MenuIcon from "@mui/icons-material/Menu";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import EmailIcon from "@mui/icons-material/Email";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import useIsAdmin from "../../../hooks/useIsAdmin";
import { config } from "../../../config";

const WorkshopMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const workshop = useSelector((state) => state.workshops.selectedWorkshop);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const viewWorkshopClicked = () => {
    closeMenu();
    navigate(`view/${workshop.workshopId}`);
  };

  const editWorkshopClicked = () => {
    closeMenu();
    navigate(`edit/${workshop.workshopId}`);
  };

  const attendeesClicked = () => {
    closeMenu();
    navigate(`reg/${workshop.workshopId}`);
  };

  const registerUserClicked = () => {
    closeMenu();
    navigate(`reg/${workshop.workshopId}?add=true`);
  };

  const registerGroupClicked = () => {
    closeMenu();
    navigate(`reg/${workshop.workshopId}?addgroup=true`);
  };

  const emailAttendeesClicked = () => {
    closeMenu();
    navigate(`reg/${workshop.workshopId}?mail=true`);
  };

  const remindAttendeesClicked = () => {
    closeMenu();
    navigate(`reg/${workshop.workshopId}?remind=true`);
  };

  const copyClicked = () => {
    closeMenu();
    navigate(`edit/new?copy=${workshop.workshopId}`);
  };

  const copyRegistrationLinkClicked = () => {
    closeMenu();
    navigator.clipboard.writeText(
      `${config.registrationClientUrl}/${workshop.workshopId}`
    );
  };

  const deleteWorkshopClicked = () => {
    closeMenu();
    setDeleteDialogOpen(false);
    dispatch(deleteWorkshop(workshop.workshopId, navigate));
  };

  //required menu functions and state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const menuClicked = (e) => {
    setMenuAnchor(e.target);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      {workshop && (
        <>
          <Box className={style.menu}>
            <Button
              onClick={viewWorkshopClicked}
              variant="contained"
              color="secondary"
              startIcon={<PageviewIcon />}
              className={style.button}
              disabled={location.pathname.includes("view")}
            >
              Preview
            </Button>
            {isAdmin && (
              <Button
                onClick={editWorkshopClicked}
                variant="contained"
                color="secondary"
                startIcon={<EditIcon />}
                className={style.button}
                disabled={location.pathname.includes("edit")}
              >
                Edit
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Groups2Icon />}
              className={style.button}
              onClick={attendeesClicked}
              disabled={location.pathname.includes("reg")}
            >
              Attendees ({workshop?.seats?.numberRegistered})
            </Button>

            <IconButton
              aria-label="menu"
              sx={{ marginLeft: "auto" }}
              color="secondary"
              onClick={menuClicked}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              anchorOrigin={{ horizontal: "left", vertical: "top" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              open={menuAnchor !== null}
              onClose={closeMenu}
            >
              <MenuItem onClick={viewWorkshopClicked}>
                <ListItemIcon>
                  <PageviewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Preview</ListItemText>
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={editWorkshopClicked}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={attendeesClicked}>
                <ListItemIcon>
                  <Groups2Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Attendees</ListItemText>
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={registerUserClicked}>
                  <ListItemIcon>
                    <PersonAddAlt1Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Register New</ListItemText>
                </MenuItem>
              )}
              {isAdmin && (
                <MenuItem onClick={registerGroupClicked}>
                  <ListItemIcon>
                    <GroupAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Register Group</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={emailAttendeesClicked}>
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mail Attendees</ListItemText>
              </MenuItem>
              <MenuItem onClick={remindAttendeesClicked}>
                <ListItemIcon>
                  <NotificationsActiveIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Send Reminder</ListItemText>
              </MenuItem>
              {isAdmin && workshop?.workshopId && <Divider />}
              {isAdmin && workshop?.workshopId && (
                <Tooltip
                  title="Copy this workshop to create a new workshop"
                  placement="left"
                >
                  <MenuItem onClick={copyClicked}>
                    <ListItemIcon>
                      <FileCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy to new</ListItemText>
                  </MenuItem>
                </Tooltip>
              )}
              <Tooltip
                title="Copy url to register for this workshop"
                placement="left"
              >
                <MenuItem onClick={copyRegistrationLinkClicked}>
                  <ListItemIcon>
                    <ContentCopyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Copy registration link</ListItemText>
                </MenuItem>
              </Tooltip>
              {isAdmin && <Divider />}
              {isAdmin && (
                <MenuItem onClick={() => setDeleteDialogOpen(true)}>
                  <ListItemIcon>
                    <DeleteOutlinedIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>

          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle>{"Delete workshop?"}</DialogTitle>
            <DialogContent>
              <Typography variant="body1">
                Deleting this workshop will mean it's data is no longer
                accessible.
              </Typography>
              <Typography variant="body1" sx={{ marginTop: "0.25rem" }}>
                If the workshop is not over yet, an email will be sent to the
                registered users saying the workshop was cancelled.
              </Typography>
              <Typography variant="h5" sx={{ marginTop: "0.5rem" }}>
                Are you sure you want to delete:
              </Typography>
              <Typography variant="h5" sx={{ marginTop: "0.25rem" }}>
                {workshop.title}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={deleteWorkshopClicked}
                variant="contained"
                color="error"
              >
                Yes Delete
              </Button>
              <Button onClick={() => setDeleteDialogOpen(false)} autoFocus>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default WorkshopMenu;
